import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { XCircle, Save, Star, Power } from 'lucide-react'
import { PageHead } from '../AdminLayout'
import { Panel, DataTable, Pager, Toolbar, Select, SearchBox, RefreshButton, Status, Tag, ConfirmButton, Drawer } from '../ui'
import { get, post, patch, qs } from '../../lib/api'
import { toast } from '../../store'
import { clsx, compact, fa, faDate, num } from '../../lib/format'

export default function Trading() {
  const [tab, setTab] = useState('orders')
  return (
    <>
      <PageHead title="بازار و معاملات" subtitle="نظارت بر سفارش‌ها، معاملات و پیکربندی بازارها" />
      <div className="mb-4 flex flex-wrap gap-1 rounded-xl border border-white/[.07] bg-white/[.03] p-1">
        {[['orders', 'سفارش‌ها'], ['trades', 'معاملات'], ['markets', 'مدیریت بازارها']].map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)}
            className={clsx('rounded-lg px-3 py-2 text-xs font-medium transition',
              tab === k ? 'bg-gradient-to-l from-brand-600 to-brand-400 text-white'
                : 'text-slate-400 hover:bg-white/[.05] hover:text-slate-200')}>
            {l}
          </button>
        ))}
      </div>
      {tab === 'orders' && <Orders />}
      {tab === 'trades' && <Trades />}
      {tab === 'markets' && <Markets />}
    </>
  )
}

function Orders() {
  const [rows, setRows] = useState<any[] | null>(null)
  const [meta, setMeta] = useState<any>(null)
  const [f, setF] = useState({ status: 'open', symbol: '', side: '', page: 1 })
  const [loading, setLoading] = useState(false)

  const load = () => {
    setLoading(true)
    get(`/admin/trading/orders${qs({ ...f, per_page: 30 })}`)
      .then((r: any) => { setRows(r.data); setMeta(r.meta) })
      .catch(() => setRows([])).finally(() => setLoading(false))
  }
  useEffect(load, [f])

  return (
    <Panel dense>
      <div className="p-4 pb-0">
        <Toolbar>
          <SearchBox value={f.symbol} onChange={(symbol) => setF({ ...f, symbol, page: 1 })} placeholder="نماد بازار مثلاً BTCIRT" />
          <Select value={f.status} onChange={(status) => setF({ ...f, status, page: 1 })}
            options={[['open', 'باز'], ['filled', 'تکمیل‌شده'], ['canceled', 'لغو‌شده'], ['', 'همه']]} />
          <Select value={f.side} onChange={(side) => setF({ ...f, side, page: 1 })}
            options={[['', 'خرید و فروش'], ['buy', 'خرید'], ['sell', 'فروش']]} />
          <RefreshButton onClick={load} loading={loading} />
          <ConfirmButton variant="down" label="لغو همه سفارش‌های باز" question="همه لغو شوند؟"
            icon={<XCircle className="h-3.5 w-3.5" />}
            onConfirm={async () => {
              try {
                const r: any = await post(`/admin/trading/orders/cancel-all${qs({ symbol: f.symbol })}`)
                toast.ok(`${fa(r.canceled)} سفارش لغو شد`); load()
              } catch (e: any) { toast.err(e.message) }
            }} />
        </Toolbar>
      </div>
      <DataTable rows={rows} loading={loading} minWidth={980} columns={[
        { key: 'id', header: '#', cell: (r) => <span className="num text-slate-500">{fa(r.id)}</span> },
        { key: 'u', header: 'کاربر', cell: (r) => <Link to={`/admin/users/${r.userId}`} className="num text-brand-300 hover:underline" dir="ltr">{r.email}</Link> },
        { key: 's', header: 'بازار', cell: (r) => <span className="num text-slate-200" dir="ltr">{r.symbol}</span> },
        { key: 'sd', header: 'سمت', cell: (r) => <span className={r.side === 'buy' ? 'text-up' : 'text-down'}>{r.side === 'buy' ? 'خرید' : 'فروش'}</span> },
        { key: 't', header: 'نوع', cell: (r) => <span className="text-slate-400">{{ limit: 'حد', market: 'بازار', stop: 'حد ضرر' }[r.type] ?? r.type}</span> },
        { key: 'p', header: 'قیمت', cell: (r) => <span className="num text-slate-300">{num(r.price)}</span> },
        { key: 'a', header: 'مقدار / پرشده', cell: (r) => <span className="num text-slate-300">{num(r.amount, 6)} / {num(r.filled, 6)}</span> },
        { key: 'v', header: 'ارزش (تومان)', cell: (r) => <span className="num text-slate-400">{compact(r.totalIrt)}</span> },
        { key: 'st', header: 'وضعیت', cell: (r) => <Status value={r.status} /> },
        { key: 'd', header: 'زمان', cell: (r) => <span className="text-slate-500">{faDate(r.createdAt)}</span> },
        {
          key: 'x', header: '', cell: (r) => ['open', 'partial'].includes(r.status) && (
            <ConfirmButton variant="ghost" label="لغو" question="لغو شود؟"
              onConfirm={async () => {
                try { await post(`/admin/trading/orders/${r.id}/cancel`); toast.ok('سفارش لغو شد'); load() }
                catch (e: any) { toast.err(e.message) }
              }} />
          ),
        },
      ]} />
      <Pager meta={meta} onPage={(page) => setF({ ...f, page })} />
    </Panel>
  )
}

function Trades() {
  const [rows, setRows] = useState<any[] | null>(null)
  const [meta, setMeta] = useState<any>(null)
  const [f, setF] = useState({ symbol: '', kind: '', page: 1 })
  const [loading, setLoading] = useState(false)

  const load = () => {
    setLoading(true)
    get(`/admin/trading/trades${qs({ ...f, per_page: 30 })}`)
      .then((r: any) => { setRows(r.data); setMeta(r.meta) })
      .catch(() => setRows([])).finally(() => setLoading(false))
  }
  useEffect(load, [f])

  return (
    <Panel dense>
      <div className="p-4 pb-0">
        <Toolbar>
          <SearchBox value={f.symbol} onChange={(symbol) => setF({ ...f, symbol, page: 1 })} placeholder="نماد بازار" />
          <Select value={f.kind} onChange={(kind) => setF({ ...f, kind, page: 1 })}
            options={[['', 'همه'], ['trade', 'معامله'], ['convert', 'تبدیل']]} />
          <RefreshButton onClick={load} loading={loading} />
        </Toolbar>
      </div>
      <DataTable rows={rows} loading={loading} minWidth={880} columns={[
        { key: 'id', header: '#', cell: (r) => <span className="num text-slate-500">{fa(r.id)}</span> },
        { key: 'u', header: 'کاربر', cell: (r) => <Link to={`/admin/users/${r.userId}`} className="num text-brand-300 hover:underline" dir="ltr">{r.email}</Link> },
        { key: 's', header: 'بازار', cell: (r) => <span className="num text-slate-200" dir="ltr">{r.symbol}</span> },
        { key: 'sd', header: 'سمت', cell: (r) => <span className={r.side === 'buy' ? 'text-up' : r.side === 'sell' ? 'text-down' : 'text-brand-200'}>{r.side === 'buy' ? 'خرید' : r.side === 'sell' ? 'فروش' : 'تبدیل'}</span> },
        { key: 'p', header: 'قیمت', cell: (r) => <span className="num text-slate-300">{num(r.price, 6)}</span> },
        { key: 'a', header: 'مقدار', cell: (r) => <span className="num text-slate-300">{num(r.amount, 6)}</span> },
        { key: 't', header: 'مبلغ', cell: (r) => <span className="num text-white">{num(r.total)}</span> },
        { key: 'f', header: 'کارمزد', cell: (r) => <span className="num text-up">{num(r.fee, 8)}</span> },
        { key: 'd', header: 'زمان', cell: (r) => <span className="text-slate-500">{faDate(r.createdAt)}</span> },
      ]} />
      <Pager meta={meta} onPage={(page) => setF({ ...f, page })} />
    </Panel>
  )
}

function Markets() {
  const [data, setData] = useState<any>(null)
  const [q, setQ] = useState('')
  const [edit, setEdit] = useState<any>(null)
  const [loading, setLoading] = useState(false)

  const load = () => {
    setLoading(true)
    get('/admin/trading/markets').then(setData).catch(() => {}).finally(() => setLoading(false))
  }
  useEffect(load, [])

  const rows = (data?.markets ?? []).filter((m: any) =>
    !q || m.symbol.toLowerCase().includes(q.toLowerCase()) || m.faName.includes(q))

  const toggle = async (m: any, field: string) => {
    try { await patch(`/admin/trading/markets/${m.symbol}`, { [field]: !m[field === 'enabled' ? 'enabled' : 'featured'] })
      toast.ok('به‌روزرسانی شد'); load() } catch (e: any) { toast.err(e.message) }
  }

  return (
    <Panel dense
      action={data && (
        <span className="num text-[11px] text-slate-500">
          پیش‌فرض: سازنده {num(data.defaults.makerFee * 100, 3)}٪ · بازارگیر {num(data.defaults.takerFee * 100, 3)}٪ ·
          حداقل سفارش {compact(data.defaults.minOrderIrt)}
        </span>
      )}>
      <div className="p-4 pb-0">
        <Toolbar>
          <SearchBox value={q} onChange={setQ} placeholder="جستجوی بازار…" />
          <RefreshButton onClick={load} loading={loading} />
        </Toolbar>
      </div>
      <DataTable rows={rows} loading={loading} minWidth={900} columns={[
        { key: 's', header: 'بازار', cell: (r: any) => <div><div className="text-slate-100">{r.faName}</div><div className="num text-[11px] text-slate-500" dir="ltr">{r.symbol}</div></div> },
        { key: 'p', header: 'قیمت', cell: (r: any) => <span className="num text-slate-200">{num(r.last)}</span> },
        { key: 'ch', header: 'تغییر', cell: (r: any) => <span className={clsx('num', r.change >= 0 ? 'text-up' : 'text-down')}>{num(r.change, 2)}٪</span> },
        { key: 'v', header: 'ارزش معاملات', cell: (r: any) => <span className="num text-slate-400">{compact(r.quoteVolume)}</span> },
        { key: 'f', header: 'کارمزد اختصاصی', cell: (r: any) => (r.makerFee !== null || r.takerFee !== null)
            ? <span className="num text-gold">{num((r.makerFee ?? 0) * 100, 3)}٪ / {num((r.takerFee ?? 0) * 100, 3)}٪</span>
            : <span className="text-slate-600">پیش‌فرض</span> },
        { key: 'm', header: 'حداقل سفارش', cell: (r: any) => r.minOrderIrt ? <span className="num text-slate-300">{compact(r.minOrderIrt)}</span> : <span className="text-slate-600">پیش‌فرض</span> },
        { key: 'st', header: 'وضعیت', cell: (r: any) => <div className="flex gap-1">
            {r.enabled ? <Tag kind="up">فعال</Tag> : <Tag kind="down">غیرفعال</Tag>}
            {r.featured && <Tag kind="gold">ویژه</Tag>}
          </div> },
        {
          key: 'x', header: 'اقدام', cell: (r: any) => (
            <div className="flex flex-wrap gap-1.5">
              <button onClick={() => toggle(r, 'enabled')} className="btn-ghost !px-2 !py-1.5 text-[11px]">
                <Power className="h-3 w-3" /> {r.enabled ? 'غیرفعال' : 'فعال'}
              </button>
              <button onClick={() => toggle(r, 'featured')} className="btn-ghost !px-2 !py-1.5 text-[11px]">
                <Star className={clsx('h-3 w-3', r.featured && 'fill-gold text-gold')} />
              </button>
              <button onClick={() => setEdit(r)} className="btn-ghost !px-2 !py-1.5 text-[11px]">تنظیم</button>
            </div>
          ),
        },
      ]} />

      <Drawer open={!!edit} onClose={() => setEdit(null)} title={`تنظیمات بازار ${edit?.symbol ?? ''}`}>
        {edit && <MarketForm m={edit} onDone={() => { setEdit(null); load() }} />}
      </Drawer>
    </Panel>
  )
}

function MarketForm({ m, onDone }: { m: any; onDone: () => void }) {
  const [f, setF] = useState({
    maker_fee: m.makerFee ?? '', taker_fee: m.takerFee ?? '',
    min_order_irt: m.minOrderIrt ?? '', note: m.note ?? '',
  })
  const save = async () => {
    const body: any = { note: f.note }
    if (f.maker_fee !== '') body.maker_fee = Number(f.maker_fee)
    if (f.taker_fee !== '') body.taker_fee = Number(f.taker_fee)
    if (f.min_order_irt !== '') body.min_order_irt = Number(f.min_order_irt)
    try { await patch(`/admin/trading/markets/${m.symbol}`, body); toast.ok('ذخیره شد'); onDone() }
    catch (e: any) { toast.err(e.message) }
  }
  return (
    <>
      <p className="mb-4 text-[11px] leading-6 text-slate-500">
        مقادیر خالی یعنی استفاده از تنظیمات پیش‌فرض سامانه. کارمزد را به صورت اعشاری وارد کنید
        (مثلاً ۰٫۰۰۱ برابر ۰٫۱ درصد است).
      </p>
      {([['maker_fee', 'کارمزد سازنده'], ['taker_fee', 'کارمزد بازارگیر'],
         ['min_order_irt', 'حداقل ارزش سفارش (تومان)']] as const).map(([k, l]) => (
        <div key={k} className="mb-3">
          <label className="label">{l}</label>
          <input className="input num" dir="ltr" placeholder="پیش‌فرض" value={(f as any)[k]}
            onChange={(e) => setF({ ...f, [k]: e.target.value.replace(/[^\d.]/g, '') })} />
        </div>
      ))}
      <div className="mb-4">
        <label className="label">یادداشت</label>
        <input className="input" value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} />
      </div>
      <div className="flex gap-2">
        <button onClick={save} className="btn-primary flex-1 !py-3"><Save className="h-4 w-4" /> ذخیره</button>
        <button onClick={async () => {
          try { await patch(`/admin/trading/markets/${m.symbol}`, { clear_fees: true }); toast.ok('بازنشانی شد'); onDone() }
          catch (e: any) { toast.err(e.message) }
        }} className="btn-ghost !py-3 text-xs">بازنشانی به پیش‌فرض</button>
      </div>
    </>
  )
}
